import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { setupRedirectListener } from "./services/redirectHandler";

// Constants for domains
const APP_DOMAIN = "app.richs.my";
const LINK_DOMAIN = "link.richs.my";
const REDIRECT_DOMAIN = "richs.com.my";

// Check if we're in development or production
const isDevelopment = () => {
  return !window.location.hostname.includes("richs.my");
};

// Determine the current domain type
const getCurrentDomainType = () => {
  const hostname = window.location.hostname;
  const path = window.location.pathname;

  // Special case for CodeSandbox testing with /debug/
  if (path.startsWith("/debug/")) {
    console.log("Debug mode detected, treating as link domain");
    return "link";
  }

  // In development mode, treat direct shortcodes as link domain
  if (isDevelopment() && path.length > 1 && !path.includes("/")) {
    console.log("Direct shortcode detected, treating as link domain");
    return "link";
  }

  if (isDevelopment()) {
    // In development, check path to simulate different domains for testing
    if (path.startsWith("/app")) {
      return "app";
    } else if (path.startsWith("/link")) {
      return "link";
    } else {
      // IMPORTANT CHANGE: Default to "link" for paths that look like shortcodes
      const shortCodePattern = /^\/[a-zA-Z0-9]{6,7}$/;
      if (shortCodePattern.test(path)) {
        console.log("Shortcode path detected, treating as link domain");
        return "link";
      }
      return "app"; // Default to app in development
    }
  }

  // In production, check actual hostname
  if (hostname.includes(APP_DOMAIN)) {
    return "app";
  } else if (hostname.includes(LINK_DOMAIN)) {
    return "link";
  }

  return "unknown";
};

// Get domain information
export const domainInfo = {
  type: getCurrentDomainType(),
  isApp: getCurrentDomainType() === "app",
  isLink: getCurrentDomainType() === "link",
  isDevelopment: isDevelopment(),
  domains: {
    app: isDevelopment()
      ? `${window.location.origin}/app`
      : `https://${APP_DOMAIN}`,
    link: isDevelopment()
      ? `${window.location.origin}/link`
      : `https://${LINK_DOMAIN}`,
    redirect: `https://${REDIRECT_DOMAIN}`,
  },
};

// Log domain information for debugging
console.log("Domain information:", domainInfo);

// Handle URL redirects before rendering the app
// This checks if the current URL is a short URL and redirects if needed
const initializeApp = async () => {
  // Check for debug mode in CodeSandbox
  const isDebugMode = window.location.pathname.startsWith("/debug/");

  // Check if this is a direct shortcode access (looks like 6-7 character code)
  const path = window.location.pathname;
  const isShortCodePath = /^\/[a-zA-Z0-9]{6,7}$/.test(path);

  // Set up redirect handling for link domain or direct shortcode access
  if (domainInfo.isLink || isShortCodePath) {
    console.log(
      "Link domain or shortcode detected, setting up redirect handler"
    );
    try {
      // In debug mode, we want to render the app even after handling redirect logic
      const redirected = await setupRedirectListener();
      if (redirected && !isDebugMode) {
        console.log("Redirect handler performed a redirect");
        return; // Don't render the app if a redirect was performed (except in debug mode)
      }
    } catch (err) {
      console.error("Error in redirect handler:", err);
    }
  }

  // Only render the app if we didn't redirect or if we're in debug mode
  const rootElement = document.getElementById("root");
  const root = createRoot(rootElement);

  // Make domainInfo available to the app
  window.domainInfo = domainInfo;

  root.render(
    <StrictMode>
      <App domainInfo={domainInfo} />
    </StrictMode>
  );
};

// Start the application
initializeApp();
